import { EmptyActionsIcon, IRowNode, InformationState } from '@access-erp/ui-components';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useGetBundlesHistory } from '../../hooks/useGetBundlesHistory';

import searchStyles from './history-search.module.scss';
import HistorySearchHeader from '@/components/common/HistorySearch/HistorySearchHeader';
import styles from './../../../../components/common/HistoryCard/history.module.scss';
import { BundleOutput } from '@/graphql/contracts/_generated_/contracts';
import HistoryCard from '@/components/common/HistoryCard/HistoryCard';
import Spinner from '@/components/common/Spinner/Spinner';
import { User } from '@/hooks/identity/responseModels/user';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { useHistorySearch } from '@/pages/ContractDetails/RightPanel/History/hooks/useHistorySearch';

interface HistoryProps {
  selectedBundles: IRowNode<BundleOutput>[];
}

export default function History({ selectedBundles }: HistoryProps) {
  const { getBundlesHistory, bundlesHistory, isLoading } = useGetBundlesHistory();
  const { searchUsers, isLoadingSearchUsers } = useSearchUsers();
  const [users, setUsers] = useState<User[]>([]);
  const tabStyle = styles['tab-wrapper'];
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const {
    searchTerm,
    filteredHistoryData,
    handleSearchChange,
    dateFilter,
    handleDateFilterChange,
  } = useHistorySearch({
    auditHistoryData: (bundlesHistory as any) ?? [],
    isLoadingSearchUsers,
    users,
  });

  useEffect(() => {
    if (
      selectedBundles &&
      selectedBundles.length > 0 &&
      selectedBundles[0]?.data?.id
    ) {
      getBundlesHistory(selectedBundles[0].data.id);
    }
  }, [selectedBundles]);

  useEffect(() => {
    if (!isLoading && bundlesHistory?.length) {
      const getUsers = async () => {
        const userIds = bundlesHistory
          .map((history) => history.eventSourceId)
          .filter((id): id is string => typeof id === 'string' && !!id);
        const uniqueUserIds = new Set(userIds);
        const searchResult = await searchUsers({ ids: [...uniqueUserIds] });
        setUsers(searchResult);
      };

      getUsers();
    }
  }, [bundlesHistory, isLoading, searchUsers]);

  useLayoutEffect(() => {
    const el = wrapperRef.current as HTMLElement | null;
    const parent = el?.parentElement as HTMLElement | null;
    if (parent) {
      parent.style.paddingTop = '0px';
    }
  }, [bundlesHistory]);

  if (!selectedBundles || selectedBundles.length === 0) {
    return (
      <InformationState
        icon={<EmptyActionsIcon />}
        title="There is no data to show"
        description="Select a bundle to see its history"
        marginTop={14.6}
      />
    );
  }

  if (Array.isArray(bundlesHistory) && bundlesHistory.length === 0) {
    return (
      <div className={tabStyle}>
        <div className="d-flex justify-content-center">Bundle has no history yet</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={tabStyle}>
        <div className="d-flex justify-content-center mt-40">
          <Spinner />
        </div>
      </div>
    );
  }
  return (
    <div ref={wrapperRef} className="historyWrapper">
      <HistorySearchHeader
        title="History"
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        date={dateFilter.startDate}
        onDateChange={(d) => handleDateFilterChange(d, null)}
        showClear={!!dateFilter.startDate}
      />

      <div className={tabStyle}>
        {filteredHistoryData?.map((el) => {
          const user = users.find((u) => u.Id == el.eventSourceId);
          return (
            <div key={el.changeSetID + user?.Id} className="mb-40">
              <HistoryCard historyItem={el} user={user} isLoading={isLoadingSearchUsers} />
            </div>
          );
        })}
      </div>

      {(searchTerm.trim() || dateFilter.startDate) &&
        (filteredHistoryData?.length ?? 0) === 0 &&
        (bundlesHistory?.length ?? 0) > 0 && !isLoadingSearchUsers && (
          <div className={searchStyles.noResultsMessage}>
            <span>No history records found matching your filters.</span>
          </div>
      )}
    </div>
  );
}
